import { useQuery } from '@tanstack/react-query'
import { vitalSignsService } from '../services/vitalSignsService'
import { VitalSigns } from '../types/vitalSigns'
import { getDisplayValue, formatTemperature, formatHeight, formatWeight } from '../utils/vitalSignsUtils'
import { UnitPreference } from '../types/vitalSigns'
import { formatDate } from '../utils/locale'
import { MaskedField } from './MaskedField'
import './VitalSignsSummary.css'

interface VitalSignsSummaryProps {
  patientId: string
  userPreference?: UnitPreference
  onAddVitals?: () => void
}

export function VitalSignsSummary({ patientId, userPreference = 'metric' }: VitalSignsSummaryProps) {
  const { data: vitalSigns, isLoading } = useQuery({
    queryKey: ['vital-signs', 'patient', patientId],
    queryFn: () => vitalSignsService.getByPatient(patientId),
    enabled: !!patientId,
  })

  if (isLoading) {
    return <div className="vital-signs-summary loading">Loading...</div>
  }

  if (!vitalSigns || vitalSigns.length === 0) {
    return (
      <div className="vital-signs-summary empty">
        <p>No vital signs recorded</p>
      </div>
    )
  }

  return (
    <MaskedField
      field="vital-signs"
      value={vitalSigns}
      patientId={patientId}
      confidentialityLevel="restricted"
    >
      {(canAccess, maskedVitalSigns, requestEmergencyAccess) => {
        if (!canAccess) {
          return (
            <div className="vital-signs-summary empty">
              <p>You do not have permission to view vital signs.</p>
              {requestEmergencyAccess && (
                <button
                  onClick={requestEmergencyAccess}
                  className="btn-secondary btn-small"
                  style={{ marginTop: '1rem' }}
                >
                  Request Emergency Access
                </button>
              )}
            </div>
          )
        }

        // Find the most recent value for each vital sign type across all records
        const getLatestVital = <T,>(getValue: (vs: VitalSigns) => T | undefined): { value: T; date: string } | null => {
          for (const vs of maskedVitalSigns) {
            const value = getValue(vs)
            // Treat both undefined and null as "no value"
            if (value !== undefined && value !== null) {
              return { value, date: vs.recordedAt || vs.createdAt || '' }
            }
          }
          return null
        }

        const latestBloodPressure = getLatestVital((vs) => vs.bloodPressure)
        const latestHeartRate = getLatestVital((vs) => vs.heartRate)
        const latestTemperature = getLatestVital((vs) => vs.temperature)
        const latestRespiratoryRate = getLatestVital((vs) => vs.respiratoryRate)
        const latestWeight = getLatestVital((vs) => vs.weight)
        const latestHeight = getLatestVital((vs) => vs.height)

        return (
          <div className="vital-signs-summary">
      <div className="vital-signs-summary-header">
        <h3>Latest Vital Signs</h3>
      </div>
      <div className="vital-signs-summary-grid">
        {latestBloodPressure && (
          <div className="vital-sign-item">
            <div className="vital-sign-item-content">
              <label>Blood Pressure</label>
              <div className="vital-sign-value">
                {latestBloodPressure.value.systolic}/{latestBloodPressure.value.diastolic} mmHg
                <span className="vital-sign-date">{formatDate(latestBloodPressure.date)}</span>
              </div>
            </div>
          </div>
        )}
        {latestHeartRate && (
          <div className="vital-sign-item">
            <div className="vital-sign-item-content">
              <label>Heart Rate</label>
              <div className="vital-sign-value">
                {latestHeartRate.value.value} bpm
                <span className="vital-sign-date">{formatDate(latestHeartRate.date)}</span>
              </div>
            </div>
          </div>
        )}
        {latestTemperature && (
          <div className="vital-sign-item">
            <div className="vital-sign-item-content">
              <label>Temperature</label>
              <div className="vital-sign-value">
                {formatTemperature(
                  getDisplayValue(latestTemperature.value, userPreference) || latestTemperature.value.value,
                  userPreference === 'imperial' ? 'fahrenheit' : 'celsius',
                )}
                <span className="vital-sign-date">{formatDate(latestTemperature.date)}</span>
              </div>
            </div>
          </div>
        )}
        {latestRespiratoryRate && (
          <div className="vital-sign-item">
            <div className="vital-sign-item-content">
              <label>Respiratory Rate</label>
              <div className="vital-sign-value">
                {latestRespiratoryRate.value.value} /min
                <span className="vital-sign-date">{formatDate(latestRespiratoryRate.date)}</span>
              </div>
            </div>
          </div>
        )}
        {latestWeight && (
          <div className="vital-sign-item">
            <div className="vital-sign-item-content">
              <label>Weight</label>
              <div className="vital-sign-value">
                {formatWeight(
                  latestWeight.value.value,
                  latestWeight.value.unit as 'kg' | 'lb' | 'stone',
                  userPreference,
                )}
                <span className="vital-sign-date">{formatDate(latestWeight.date)}</span>
              </div>
            </div>
          </div>
        )}
        {latestHeight && (
          <div className="vital-sign-item">
            <div className="vital-sign-item-content">
              <label>Height</label>
              <div className="vital-sign-value">
                {formatHeight(
                  latestHeight.value.value,
                  latestHeight.value.unit as 'cm' | 'in',
                  userPreference,
                )}
                <span className="vital-sign-date">{formatDate(latestHeight.date)}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
        )
      }}
    </MaskedField>
  )
}
